import {
  A1SocketG,
  A4Electricalwires,
  A4Electricitymeter,
  B1BatteryFull,
  B2Cloudandsunday,
  DynamicPriceIcon,
  E1House,
  I1ElectricalChargingStation,
} from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useMemo } from 'react'
import { getBatteriesDispatchRoute } from 'src/Libs/batteries'
import { getEmergencyPowerOverviewRoute } from 'src/Libs/emergency-power'
import {
  FeatureElements,
  getHasElementNewContent,
  getHaveElementsNewContent,
  useDynamicSystemSettingsElementKeys,
  useHasNewFeature,
} from 'src/Libs/new-content'
import { getPowerMeterDetailsDispatchRoute } from 'src/Libs/power-meters'
import { getBaseRoute as getSolarModuleBaseRoute } from 'src/Libs/solar/routes'
import { getBaseRoute as getWallboxBaseRoute } from 'src/Libs/wallboxes/routes'
import { makeStyles } from 'tss-react/mui'
import { SystemSettingsDataCardModel } from '../types'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { EmsProductType } from 'src/Routes/Installation/InstallationModel'

const useStyles = makeStyles()(() => ({
  icon: {
    fontSize: '52px',
  },
}))

const useE3dcSystemSettingsOverviewCardConfigs = (): SystemSettingsDataCardModel[] => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { installationId: activeInstallation, storage, storageId: activeStorage } = useActiveInstallationContext()
  const { currentFeatureContentVersion } = useHasNewFeature()
  const { smartFunctionsElementKeys } = useDynamicSystemSettingsElementKeys()
  const isQntm = storage?.product === EmsProductType.qntm

  const settingsCardConfigs: SystemSettingsDataCardModel[] = useMemo(
    () => [
      ...(!isQntm
        ? [
            {
              title: t('framework.solar'),
              description: t('systemSettings.solar.pageDescription'),
              icon: <B2Cloudandsunday fontSize="inherit" className={classes.icon} />,
              path: getSolarModuleBaseRoute(activeInstallation?.toString(), activeStorage),
              hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertySolarSideBarElement),
              elementKey: FeatureElements.PropertySolarSideBarElement,
            },
          ]
        : []),
      {
        title: t('framework.battery'),
        description: t('systemSettings.battery.pageDescription'),
        icon: <B1BatteryFull fontSize={'inherit'} className={classes.icon} />,
        path: getBatteriesDispatchRoute(activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyBatterySideBarElement),
        elementKey: FeatureElements.PropertyBatterySideBarElement,
      },
      {
        title: t('framework.powermeter'),
        description: t('systemSettings.powerMeter.pageDescription'),
        icon: <A4Electricitymeter className={classes.icon} />,
        path: getPowerMeterDetailsDispatchRoute(activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyPowermeterSideBarElement),
        elementKey: FeatureElements.PropertyPowermeterSideBarElement,
      },
      {
        title: t('framework.wallbox'),
        description: t('systemSettings.wallbox.pageDescription'),
        icon: <I1ElectricalChargingStation className={classes.icon} />,
        path: getWallboxBaseRoute(activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyWallboxSideBarElement),
        elementKey: FeatureElements.PropertyWallboxSideBarElement,
      },
      {
        title: t('framework.smartHome'),
        description: t('systemSettings.smartFunctions.pageDescription'),
        icon: <E1House className={classes.icon} />,
        /* TODO: revert the changes made in CPE-1861 */
        path: `/installation/${activeInstallation}/${activeStorage}/properties/smart-functions/smart-charge`,
        hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, smartFunctionsElementKeys),
        elementKey: FeatureElements.PropertySmartFunctionSideBarElement,
      },
      {
        title: t('framework.tariffs'),
        description: t('systemSettings.tariffs.pageDescription'),
        icon: <DynamicPriceIcon className={classes.icon} />,
        path: `/installation/${activeInstallation}/${activeStorage}/properties/tariffs`,
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyTariffsSideBarElement),
        elementKey: FeatureElements.PropertyTariffsSideBarElement,
      },
      ...(!isQntm
        ? [
            {
              title: t('framework.emergency-power'),
              description: t('systemSettings.emergencyPower.pageDescription'),
              icon: <A1SocketG className={classes.icon} />,
              path: getEmergencyPowerOverviewRoute(activeInstallation?.toString(), activeStorage),
              hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyEmergencyPowerSideBarElement),
              elementKey: FeatureElements.PropertyEmergencyPowerSideBarElement,
            },
          ]
        : []),
      {
        title: t('framework.installation.installation'),
        description: t('systemSettings.installation.pageDescription'),
        icon: <A4Electricalwires className={classes.icon} />,
        path: `/installation/${activeInstallation}/${activeStorage}/properties/installation`,
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyInstallationSideBarElement),
        elementKey: FeatureElements.PropertyInstallationSideBarElement,
      },
    ],
    [activeInstallation, activeStorage, classes.icon, currentFeatureContentVersion, smartFunctionsElementKeys, t, isQntm]
  )

  return settingsCardConfigs
}

export default useE3dcSystemSettingsOverviewCardConfigs
